import { useCallback, useMemo } from 'react';

import { isEqual } from 'lodash';
import { useController } from 'react-hook-form';
import type { Control } from 'react-hook-form';

interface UseArrayControllerProps {
  control: Control;
  name: string;
  defaultValue?: unknown[];
}

interface UseArrayControllerReturn {
  values: unknown[];
  setValue: (nextValue: unknown[]) => void;
  isSelected: (value: unknown) => boolean;
  toggleOption: (value: unknown) => void;
  removeOption: (value: unknown) => void;
  reset: VoidFunction | undefined;
}

const normalize = (v: unknown): unknown[] => {
  if (Array.isArray(v)) return v;
  if (v == null || v === '') return [];
  if (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean') return [v];
  return [];
};

export function useArrayController({
  control,
  name,
  defaultValue = [],
}: UseArrayControllerProps): UseArrayControllerReturn {
  const { field } = useController({
    control,
    name,
    defaultValue,
  });

  const values = normalize(field.value);

  const setValue = useCallback(
    (nextValue: unknown[]) => {
      field.onChange(nextValue);
    },
    [field],
  );

  const isSelected = useCallback(
    (value: unknown) => {
      return normalize(field.value).some((v) => isEqual(v, value));
    },
    [field],
  );

  const toggleOption = useCallback(
    (value: unknown) => {
      const arr = normalize(field.value);
      const exists = arr.some((v) => isEqual(v, value));

      field.onChange(
        exists ? arr.filter((v) => !isEqual(v, value)) : [...arr, value],
      );
    },
    [field],
  );

  const removeOption = useCallback(
    (value: unknown) => {
      field.onChange(
        normalize(field.value).filter((v) => !isEqual(v, value)),
      );
    },
    [field],
  );

  const reset = useMemo(() => {
    const arr = normalize(field.value);
    return arr.length > 0 ? () => field.onChange([]) : undefined;
  }, [field]);

  return {
    values,
    setValue,
    isSelected,
    toggleOption,
    removeOption,
    reset,
  };
}
